import { useEffect, useState } from "react";
import { TrendingUp, Sparkles, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorText } from "@/lib/api";
import { MONTHS, fmtPct } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ASSUMPTION_LABELS = {
  adr_growth_pct: "ADR growth", occupancy_growth_pct: "Occupancy growth", fnb_price_growth_pct: "F&B price increase", covers_growth_pct: "Covers / guests growth",
  other_revenue_growth_pct: "Other revenue growth", umr_increase_pct: "UMR / payroll increase", inflation_pct: "Inflation (operating expenses)",
};

const fmtV = (v, avg) => {
  if (v === null || v === undefined) return "-";
  if (avg) return Math.abs(v) <= 1.5 ? `${(v * 100).toFixed(2)}%` : Math.round(v).toLocaleString("en-US");
  const a = Math.abs(v);
  const s = a >= 1e9 ? `${(a / 1e9).toFixed(2)} B` : a >= 1e6 ? `${(a / 1e6).toFixed(1)} M` : Math.round(a).toLocaleString("en-US");
  return v < 0 ? `(${s})` : s;
};
const pctCls = (v) => (v === null || v === undefined ? "text-slate-400" : v < 0 ? "text-red-600" : "text-emerald-700");

export const TrendPanel = ({ projectId, current, disabled }) => {
  const { can } = useAuth();
  const [t, setT] = useState(null);
  const [kpi, setKpi] = useState("revenue");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/projects/${projectId}/trend`).then((r) => setT(r.data)).catch(() => setT(null));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!t) return null;
  const hasHistory = t.history_years.length > 0;
  const sel = t.kpis.find((k) => k.key === kpi) || t.kpis[0];
  const years = sel ? Object.keys(sel.by_year).sort() : [];
  const cur = String(t.actual_year);
  const ly = String(t.actual_year - 1);
  const analysis = t.analysis;
  const suggested = analysis?.suggested_assumptions && Object.keys(analysis.suggested_assumptions).length ? analysis.suggested_assumptions : Object.fromEntries(Object.entries(t.suggestions).map(([k, s]) => [k, s.value]));
  const analyze = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${projectId}/trend/analyze`);
      setT({ ...t, analysis: r.data });
      toast.success("Historical trend analysis generated");
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6" data-testid="trend-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900 inline-flex items-center gap-2"><TrendingUp size={16} className="text-emerald-700" /> Historical Trend Analysis</h3>
          <p className="text-xs text-slate-500 mt-1" data-testid="trend-years">
            {hasHistory ? <>Years analysed: <span className="font-medium text-slate-700">{t.years.join(", ")}</span> · {t.actual_year} actuals through {MONTHS[t.last_actual_month - 1] || "-"}. Same-month comparison (e.g. {MONTHS[0]} {ly} vs {MONTHS[0]} {cur}), YTD year-over-year and multi-year CAGR.</>
              : <>No prior-year files yet — upload {ly} (and {t.actual_year - 2}, {t.actual_year - 3} if available) under <span className="font-medium">Step 1 → Prior-year actuals</span> to unlock year-over-year trend analysis.</>}
          </p>
        </div>
        {hasHistory && can("edit") && (
          <Button type="button" size="sm" onClick={analyze} disabled={busy || disabled} data-testid="trend-analyze-button" className="bg-slate-900 hover:bg-emerald-700 text-white h-9">
            <Sparkles size={14} className="mr-1.5" /> {busy ? "Analysing…" : analysis ? "Re-run AI analysis" : "Analyse with AI"}
          </Button>
        )}
      </div>

      {hasHistory && (
        <>
          <div className="mt-4 overflow-auto" data-testid="trend-summary-table">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left">
                  <th className="py-1.5 pr-3">KPI</th>
                  {t.history_years.filter((y) => y !== t.actual_year - 1).map((y) => <th key={y} className="py-1.5 px-2 text-right">FY {y}</th>)}
                  <th className="py-1.5 px-2 text-right">FY {ly}</th>
                  <th className="py-1.5 px-2 text-right">YTD {ly}</th>
                  <th className="py-1.5 px-2 text-right">YTD {cur}</th>
                  <th className="py-1.5 px-2 text-right">YoY YTD</th>
                  <th className="py-1.5 px-2 text-right">CAGR</th>
                </tr>
              </thead>
              <tbody>
                {t.kpis.map((k) => (
                  <tr key={k.key} onClick={() => setKpi(k.key)} className={`border-t border-slate-100 cursor-pointer ${sel?.key === k.key ? "bg-emerald-50/60" : "hover:bg-slate-50"}`} data-testid={`trend-kpi-row-${k.key}`}>
                    <td className="py-1.5 pr-3 font-medium text-slate-800">{k.label}</td>
                    {t.history_years.filter((y) => y !== t.actual_year - 1).map((y) => <td key={y} className="py-1.5 px-2 text-right font-mono">{fmtV(k.totals[String(y)], k.avg_type)}</td>)}
                    <td className="py-1.5 px-2 text-right font-mono">{fmtV(k.totals[ly], k.avg_type)}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{fmtV(k.ytd[ly], k.avg_type)}</td>
                    <td className="py-1.5 px-2 text-right font-mono font-semibold">{fmtV(k.ytd[cur], k.avg_type)}</td>
                    <td className={`py-1.5 px-2 text-right font-mono ${pctCls(k.yoy_ytd_pct)}`} data-testid={`trend-yoy-${k.key}`}>{fmtPct(k.yoy_ytd_pct)}</td>
                    <td className={`py-1.5 px-2 text-right font-mono ${pctCls(k.cagr_pct)}`}>{k.cagr_pct === null ? <span className="text-slate-300" title="Needs at least two full prior years">n/a</span> : fmtPct(k.cagr_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {sel && (
            <div className="mt-4 overflow-auto rounded border border-slate-100" data-testid="trend-month-table">
              <table className="min-w-full text-[11px]">
                <thead className="bg-slate-50">
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="py-1.5 px-2 text-left">{sel.label} by month</th>
                    {MONTHS.map((m) => <th key={m} className="py-1.5 px-1.5 text-right">{m}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {years.map((y) => (
                    <tr key={y} className="border-t border-slate-100" data-testid={`trend-month-row-${y}`}>
                      <td className="py-1 px-2 font-mono font-semibold text-slate-800">{y}</td>
                      {sel.by_year[y].map((v, i) => <td key={i} className={`py-1 px-1.5 text-right font-mono ${y === cur && i >= t.last_actual_month ? "text-slate-300" : "text-slate-700"}`}>{y === cur && i >= t.last_actual_month ? "·" : fmtV(v, sel.avg_type)}</td>)}
                    </tr>
                  ))}
                  <tr className="border-t border-slate-200 bg-slate-50/60" data-testid="trend-month-yoy-row">
                    <td className="py-1 px-2 text-slate-600">YoY {cur} vs {ly}</td>
                    {sel.yoy_month_pct.map((v, i) => <td key={i} className={`py-1 px-1.5 text-right font-mono ${pctCls(v)}`}>{v === null ? "" : fmtPct(v)}</td>)}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-5 grid lg:grid-cols-[1fr_320px] gap-5">
            <div data-testid="trend-ai-block">
              {analysis ? (
                <>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">AI analysis · {analysis.model?.split(":")[1] || analysis.model} · {new Date(analysis.generated_at).toLocaleDateString("en-GB")}</p>
                  <p className="text-sm text-slate-800 mt-1.5 leading-relaxed" data-testid="trend-ai-summary">{analysis.summary}</p>
                  {analysis.patterns?.length > 0 && <ul className="mt-3 space-y-1" data-testid="trend-ai-patterns">{analysis.patterns.map((x, i) => <li key={i} className="text-xs text-slate-700 flex gap-2"><Check size={12} className="text-emerald-600 mt-0.5 shrink-0" /> {x}</li>)}</ul>}
                  {analysis.anomalies?.length > 0 && <ul className="mt-2 space-y-1" data-testid="trend-ai-anomalies">{analysis.anomalies.map((x, i) => <li key={i} className="text-xs text-amber-800 flex gap-2"><AlertTriangle size={12} className="text-amber-600 mt-0.5 shrink-0" /> {x}</li>)}</ul>}
                </>
              ) : (
                <p className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded p-3" data-testid="trend-ai-empty">Run the AI analysis to get a narrative of seasonality, year-over-year patterns, anomalies and recommended assumptions based on all uploaded years. Until then the suggestions on the right are purely statistical (60% YTD YoY + 40% CAGR).</p>
              )}
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4" data-testid="trend-suggestions">
              <p className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">{analysis?.suggested_assumptions && Object.keys(analysis.suggested_assumptions).length ? "AI-recommended assumptions" : "Statistical suggestions"}</p>
              <table className="w-full text-xs mt-2">
                <tbody>
                  {Object.entries(suggested).map(([k, v]) => (
                    <tr key={k} className="border-t border-emerald-100" data-testid={`trend-suggestion-${k}`} title={analysis?.rationale?.[k] || t.suggestions[k]?.basis || ""}>
                      <td className="py-1.5 pr-2 text-slate-700">{ASSUMPTION_LABELS[k] || k}</td>
                      <td className="py-1.5 px-1 text-right font-mono text-slate-400">{current?.[k] !== undefined ? `${Number(current[k]).toFixed(2)}%` : ""}</td>
                      <td className="py-1.5 pl-1 text-right font-mono font-semibold text-emerald-800">→ {Number(v).toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-[11px] text-slate-500 border-t border-emerald-100 pt-2" data-testid="trend-reference-note">Reference only — these suggestions are not applied automatically. Review them and type the growth % you decide on into the assumption fields below.</p>
            </div>
          </div>
        </>
      )}
    </section>
  );
};
